import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createApi } from "util/ApiCreator";
import { InputContent, ProcessExecutionResponse, StepExecutionResponse } from "api/main/data-contracts";
import { ProcessExecutionAdminController } from "api/main/ProcessExecutionAdminController";
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import { hasRole } from "util/UtilityFunctions";
import { RoleNames } from "models/auth/RoleNames";
import { useAppSelector } from "redux/hooks";
import CommonConstants from "constants/CommonConstants";
import { adminTheme, statusColors } from "theme/adminTheme";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DrawIcon from "@mui/icons-material/Draw";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";

function getStatusInfo(status?: string) {
  switch (status) {
    case "IN_PROGRESS":
      return { label: "Vykdomas", colors: statusColors.in_progress };
    case "FINISHED":
      return { label: "Užbaigtas", colors: statusColors.finished };
    case "CANCELLED":
      return { label: "Atmestas", colors: statusColors.cancelled };
    case "DEAL_DONE":
      return { label: "Pinigai grąžinti", colors: statusColors.succeed };
    default:
      return { label: status || "Nežinomas", colors: statusColors.pending };
  }
}

function getStepStatusInfo(status?: string) {
  switch (status) {
    case "FINISHED":
      return { icon: <CheckCircleOutlineIcon />, colors: statusColors.finished, label: "Įvykdytas" };
    case "ACTIVE":
      return { icon: <HourglassEmptyIcon />, colors: statusColors.in_progress, label: "Aktyvus" };
    case "NOT_ACTIVE":
      return { icon: <HighlightOffIcon />, colors: statusColors.pending, label: "Neaktyvus" };
    default:
      return { icon: <HighlightOffIcon />, colors: statusColors.pending, label: "Nežinomas" };
  }
}

function getStepTypeIcon(type?: string) {
  switch (type) {
    case "IDENTIFICATION":
      return <PersonIcon />;
    case "DATA_INPUT":
      return <AssignmentIcon />;
    case "SIGN":
      return <DrawIcon />;
    case "CONFIRMATION":
      return <CheckIcon />;
    default:
      return <InfoOutlinedIcon />;
  }
}

function formatExecutors(
  executors?: ("ADMIN" | "USER")[],
  accountInfo?: { email?: string; firstName?: string; lastName?: string }
): string {
  if (!executors || executors.length === 0) return "-";

  return executors
    .map((executor) => {
      if (executor === "USER" && accountInfo) {
        const nameParts = [];
        if (accountInfo.firstName) nameParts.push(accountInfo.firstName);
        if (accountInfo.lastName) nameParts.push(accountInfo.lastName);
        if (nameParts.length > 0) {
          return accountInfo.email
            ? `${nameParts.join(" ")} (${accountInfo.email})`
            : nameParts.join(" ");
        }
        return accountInfo.email || "Vartotojas";
      }
      if (executor === "ADMIN") {
        return "Administratorius";
      }
      return executor;
    })
    .join(", ");
}

export default function ProcessExecutionAdminPage() {
  const me = useAppSelector((state) => state.me);
  const navigate = useNavigate();
  const { id } = useParams();
  const executionApi = useRef(createApi("processexecutionadmin") as ProcessExecutionAdminController);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setExecution] = useState<ProcessExecutionResponse>();

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getData() {
    setLoading(true);
    if (id) {
      setExecution(await executionApi.current.findById(+id));
    }
    setLoading(false);
  }

  const statusInfo = getStatusInfo(data?.status);
  const completedSteps = data?.stepExecutions?.filter(s => s.status === "FINISHED").length || 0;
  const totalSteps = data?.stepExecutions?.length || 1;
  const progressPercentage = Math.round((completedSteps / totalSteps) * 100);

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
      {loading && <LinearProgress sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999 }} />}

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderColor: adminTheme.colors.border,
                color: adminTheme.colors.textSecondary,
                "&:hover": {
                  borderColor: adminTheme.colors.primary,
                  bgcolor: `${adminTheme.colors.primary}08`,
                  color: adminTheme.colors.primary,
                },
              }}
            >
              Atgal
            </Button>
            <Tooltip title="Atnaujinti">
              <IconButton
                onClick={getData}
                sx={{
                  bgcolor: adminTheme.colors.background,
                  border: `1px solid ${adminTheme.colors.border}`,
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  color: adminTheme.colors.textPrimary,
                }}
              >
                {data?.process?.name || "Proceso vykdymas"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary }}>
                  Vykdymo ID: #{id}
                </Typography>
                <Chip
                  label={statusInfo.label}
                  size="small"
                  sx={{
                    bgcolor: statusInfo.colors.bg,
                    color: statusInfo.colors.text,
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>

            {/* Action Buttons */}
            {hasRole(RoleNames.ADMIN, me?.info?.roles) && (
              <Box sx={{ display: "flex", gap: 2 }}>
                {data?.status !== "CANCELLED" && data?.status !== "DEAL_DONE" && data?.status !== "FINISHED" && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={async () => {
                      if (!data?.id) return;
                      await executionApi.current.cancel(data?.id);
                      getData();
                    }}
                    sx={{
                      borderRadius: adminTheme.borderRadius.md,
                    }}
                  >
                    Baigti procesą
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Status Alert */}
        {data?.status === "CANCELLED" && (
          <Card
            sx={{
              p: 3,
              mb: 4,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${statusColors.cancelled.text}`,
              bgcolor: statusColors.cancelled.bg,
            }}
          >
            <Typography variant="h6" sx={{ color: statusColors.cancelled.text, fontWeight: 600 }}>
              Procesas atmestas
            </Typography>
          </Card>
        )}
        {data?.status === "DEAL_DONE" && (
          <Card
            sx={{
              p: 3,
              mb: 4,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${statusColors.succeed.text}`,
              bgcolor: statusColors.succeed.bg,
            }}
          >
            <Typography variant="h6" sx={{ color: statusColors.succeed.text, fontWeight: 600 }}>
              Pinigai grąžinti vartotojui
            </Typography>
          </Card>
        )}

        {/* Progress Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: adminTheme.borderRadius.lg,
                border: `1px solid ${adminTheme.colors.border}`,
                boxShadow: "none",
                transition: adminTheme.transitions.normal,
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: adminTheme.shadows.cardHover,
                  borderColor: "transparent",
                },
              }}
            >
              <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted, mb: 1 }}>
                Progresas
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: adminTheme.colors.primary }}>
                {progressPercentage}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progressPercentage}
                sx={{
                  mt: 2,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: `${adminTheme.colors.primary}20`,
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    background: adminTheme.gradients.primary,
                  },
                }}
              />
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: adminTheme.borderRadius.lg,
                border: `1px solid ${adminTheme.colors.border}`,
                boxShadow: "none",
              }}
            >
              <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted, mb: 1 }}>
                Įvykdyti žingsniai
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: adminTheme.colors.success }}>
                {completedSteps} / {totalSteps}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: adminTheme.borderRadius.lg,
                border: `1px solid ${adminTheme.colors.border}`,
                boxShadow: "none",
              }}
            >
              <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted, mb: 1 }}>
                Vartotojas
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
                {data?.accountInfoResponse?.email || "N/A"}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: adminTheme.borderRadius.lg,
                border: `1px solid ${adminTheme.colors.border}`,
                boxShadow: "none",
              }}
            >
              <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted, mb: 1 }}>
                Statusas
              </Typography>
              <Chip
                label={statusInfo.label}
                sx={{
                  bgcolor: statusInfo.colors.bg,
                  color: statusInfo.colors.text,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  px: 1,
                }}
              />
            </Card>
          </Grid>
        </Grid>

        {/* Steps */}
        <Card
          sx={{
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 3,
              borderBottom: `1px solid ${adminTheme.colors.border}`,
              bgcolor: adminTheme.colors.backgroundAlt,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
              Proceso žingsniai
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            {data?.stepExecutions?.map((step: StepExecutionResponse, index) => {
              const stepStatus = getStepStatusInfo(step.status);
              const isLast = index === (data?.stepExecutions?.length || 0) - 1;

              return (
                <Box key={step.id} sx={{ position: "relative" }}>
                  {/* Timeline connector */}
                  {!isLast && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 23,
                        top: 48,
                        bottom: -24,
                        width: 2,
                        bgcolor: step.status === "FINISHED" ? adminTheme.colors.success : adminTheme.colors.border,
                      }}
                    />
                  )}

                  <Box sx={{ display: "flex", gap: 3, mb: isLast ? 0 : 4 }}>
                    {/* Step Icon */}
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        bgcolor: stepStatus.colors.bg,
                        color: stepStatus.colors.text,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        zIndex: 1,
                      }}
                    >
                      {stepStatus.icon}
                    </Box>

                    {/* Step Content */}
                    <Card
                      sx={{
                        flex: 1,
                        p: 3,
                        borderRadius: adminTheme.borderRadius.md,
                        border: `1px solid ${adminTheme.colors.border}`,
                        boxShadow: "none",
                        bgcolor: step.status === "ACTIVE" ? `${adminTheme.colors.primary}05` : "transparent",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            <Box sx={{ color: adminTheme.colors.textMuted }}>
                              {getStepTypeIcon(step.type)}
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
                              {step.name}
                            </Typography>
                          </Box>
                          {step.step?.executors && step.step.executors.length > 0 && (
                            <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary }}>
                              Vykdo: {formatExecutors(step.step.executors, data?.accountInfoResponse)}
                            </Typography>
                          )}
                        </Box>
                        <Chip
                          label={stepStatus.label}
                          size="small"
                          sx={{
                            bgcolor: stepStatus.colors.bg,
                            color: stepStatus.colors.text,
                            fontWeight: 600,
                          }}
                        />
                      </Box>

                      {/* Step Details */}
                      <StepContent step={step} />
                    </Card>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Card>
      </Container>
    </Box>
  );
}

function StepContent({ step }: { step: StepExecutionResponse }) {
  const obj = JSON.parse(step.stepInfo || "{}");

  if (step.status !== "FINISHED") {
    return (
      <Box
        sx={{
          p: 2,
          borderRadius: adminTheme.borderRadius.sm,
          bgcolor: adminTheme.colors.backgroundAlt,
        }}
      >
        <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted, fontStyle: "italic" }}>
          Šis žingsnis dar neįvykdytas
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {step.type === "SIGN" && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload(step.executionInfo?.signingInfo?.fileId, "download.pdf")}
            sx={{
              borderColor: adminTheme.colors.primary,
              color: adminTheme.colors.primary,
              "&:hover": {
                bgcolor: `${adminTheme.colors.primary}08`,
              },
            }}
          >
            Parsisiųsti pasirašytą failą
          </Button>
        </Box>
      )}

      {step.type === "IDENTIFICATION" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 2,
            p: 2,
            borderRadius: adminTheme.borderRadius.sm,
            bgcolor: adminTheme.colors.backgroundAlt,
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
              Vardas
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {step.executionInfo?.identificationInfo?.firstName || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
              Pavardė
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {step.executionInfo?.identificationInfo?.lastName || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
              Asmens kodas
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {step.executionInfo?.identificationInfo?.personCode || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
              Identifikavosi su
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {step.executionInfo?.identificationInfo?.identificationType || "N/A"}
            </Typography>
          </Box>
        </Box>
      )}

      {step.type === "DATA_INPUT" && (
        <Box
          sx={{
            p: 2,
            borderRadius: adminTheme.borderRadius.sm,
            bgcolor: adminTheme.colors.backgroundAlt,
          }}
        >
          {Object.keys(obj).map((key) => {
            const inputContent = combineArrays(step?.step?.stepInformation?.inputContent, key);
            return (
              <Box key={key} sx={{ mb: 1, "&:last-child": { mb: 0 } }}>
                <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
                  {inputContent?.label || key}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  <InputContentDisplay content={obj[key]} />
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {step.type === "CONFIRMATION" && (
        <Box
          sx={{
            p: 2,
            borderRadius: adminTheme.borderRadius.sm,
            bgcolor: adminTheme.colors.backgroundAlt,
          }}
        >
          <Typography variant="body2" sx={{ color: adminTheme.colors.textMuted }}>
            Patvirtinta
          </Typography>
        </Box>
      )}

      {/* Execution Info */}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {step.executionInfo?.time && (
          <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
            Laikas: {step.executionInfo.time}
          </Typography>
        )}
        {step.executionInfo?.ip && (
          <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
            IP: {step.executionInfo.ip}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function InputContentDisplay({ content }: { content: any }) {
  const stringContent = JSON.stringify(content);
  const parsed = JSON.parse(stringContent);

  if (typeof parsed === "object" && Array.isArray(parsed)) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {parsed.map((item: any, index: number) => (
          <Button
            key={index}
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload(item.id, item.name)}
            sx={{
              justifyContent: "flex-start",
              borderColor: adminTheme.colors.border,
            }}
          >
            {item.name} ({item.type})
          </Button>
        ))}
      </Box>
    );
  }

  return <>{stringContent}</>;
}

export async function handleDownload(id?: number, filename?: string) {
  try {
    if (!id) {
      console.warn("NO id", id);
      return;
    }
    window.open(CommonConstants.apiBaseUrl + `/api/admin/v1/filestorage/download/${id}`, "_self");
  } catch (error) {
    console.error("Download failed", error);
  }
}

const combineArrays = (inputContent?: InputContent[][], findName?: string): InputContent | undefined => {
  if (!inputContent) return;
  let ret: InputContent[] = [];
  inputContent.forEach((group) => ret.push(...group));
  return ret.find((r) => r.name === findName);
};
